import { useCallback, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { BdsIconX } from "@buildertrend/components";

import { type Countries } from "types/countries";

import { buildSmsHref, buildTelHref } from "utilities/phone/phoneHref";

import { BTBottomSheet } from "commonComponents/btWrappers/BTBottomSheet/BTBottomSheet";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";

export interface IPhoneActionSheetProps {
    /**
     * Optional inline label preceding the phone number (e.g. "Phone" / "Cell").
     * Omit when the trigger is wrapped in a form item with its own label so the
     * label isn't duplicated.
     */
    label?: string;
    /** Visible label for the sheet header, e.g. "Phone:" or "Cell:". */
    sheetLabel?: string;
    /** Phone number used for tel:/sms: URIs and shown in the trigger and sheet header. */
    value: string;
    /** Stable testid base; "{base}", "{base}-call", "{base}-text", "{base}-close" are emitted. */
    testIdBase: string;
    /**
     * Country assumed when the phone number lacks a country prefix. Used to
     * parse `value` into E.164 form for the tel:/sms: URIs so iOS and Android
     * don't have to guess. Falls back to a digits-only strip when omitted.
     */
    defaultCountryCode?: Countries;
}

const actionRowClassName =
    "block w-full p-sm text-center rounded-base bg-gray-5 text-fg-default hover:bg-gray-15 focus:bg-gray-15";

/**
 * Renders "{label}: {value}" where the value is interactive.
 *
 * On touch-primary devices (phones and tablets — matched by the
 * `(pointer: coarse)` media query) it opens a BTBottomSheet exposing Call
 * (tel:) and Text (sms:) actions, so the user can choose between the dialer
 * and the SMS app. There is no web equivalent of an OS action sheet, so the
 * BT bottom sheet stands in for that pattern.
 *
 * On mouse-primary devices the trigger collapses to a plain `tel:` anchor;
 * desktops typically don't have a useful SMS handler, and tel: lets the OS
 * route to whatever the user has configured (FaceTime, Skype, Phone Link,
 * etc.) just like any other phone-number link.
 */
export function PhoneActionSheet({
    label,
    sheetLabel,
    value,
    testIdBase,
    defaultCountryCode,
}: IPhoneActionSheetProps) {
    const isTouchPrimary = useMediaQuery("(pointer: coarse)");
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const openSheet = useCallback(() => setIsSheetOpen(true), []);
    const closeSheet = useCallback(() => setIsSheetOpen(false), []);

    const telHref = buildTelHref(value, defaultCountryCode);
    const smsHref = buildSmsHref(value, defaultCountryCode);

    if (!isTouchPrimary) {
        return (
            <div className="inline-flex items-baseline gap-xs flex-wrap">
                {label && <span>{label}:</span>}
                <BTLink
                    linkType="tel"
                    href={telHref}
                    to=""
                    isUnderline={false}
                    data-testid={testIdBase}
                >
                    {value}
                </BTLink>
            </div>
        );
    }

    return (
        <div className="inline-flex items-baseline gap-xs flex-wrap">
            {label && <span>{label}:</span>}
            <BTLink
                to=""
                data-testid={testIdBase}
                isUnderline={false}
                onClick={openSheet}
                className="p-0 min-w-0"
            >
                {value}
            </BTLink>
            <BTBottomSheet visible={isSheetOpen} onClose={closeSheet}>
                <div className="flex flex-col gap-xs p-sm pb-12">
                    <div className="flex items-center justify-center relative gap-sm p-sm">
                        <BTButton
                            data-testid={`${testIdBase}-close`}
                            aria-label="Close"
                            type="tertiary"
                            icon={<BdsIconX />}
                            onClick={closeSheet}
                            className="absolute right-0"
                        />
                        <div className="font-semibold text-fg-default">
                            {sheetLabel && `${sheetLabel}:`} {value}
                        </div>
                    </div>
                    <BTLink
                        linkType="tel"
                        href={telHref}
                        to=""
                        isUnderline={false}
                        className={actionRowClassName}
                        onClick={closeSheet}
                        data-testid={`${testIdBase}-call`}
                        type="secondary"
                    >
                        Call
                    </BTLink>
                    <BTLink
                        linkType="sms"
                        href={smsHref}
                        to=""
                        isUnderline={false}
                        className={actionRowClassName}
                        onClick={closeSheet}
                        data-testid={`${testIdBase}-text`}
                        type="secondary"
                    >
                        Text
                    </BTLink>
                </div>
            </BTBottomSheet>
        </div>
    );
}
